import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Records the viewer's place in a Virtual Meeting session recording (TMS-1614),
// so every surface can resume it on any device. Wraps the shared video player
// and listens to its <video>; it never drives playback. A session with audio
// can be listened to instead (TMS-1825): the page then hands over the <audio>
// through useMedia, and listening records the same place as watching.
//
// Writes when playback first starts (that write is what makes the session
// "started" for CME), every `interval` ms while playing, on pause and seek, on
// leaving the page, and at the end. A session watched to the end — or to within
// the last COMPLETE_FRACTION of it — is marked completed, and finishing it
// stores the top as the place to resume, so reopening it plays from the start.
//
// Writes go out with `keepalive`, so the one sent while the page unloads (or
// while Turbo swaps it for the next session) still lands.
//
// Each write answers with the table of contents' progress as it stands after
// it, which redraws the counter, the time left and this session's row without
// a refresh (TMS-1720). A reply is dropped once the page has moved on, and when
// a later write's reply has already been drawn.
//
// A write refused because the viewer's access has ended answers with the
// notice that takes the player's place; playback stops there and nothing more
// is written.
const COMPLETE_FRACTION = 0.95
const MEDIA_SELECTOR = '[data-media-player-target="media"]'

export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 10000 }
  }

  // The handlers exist from the start: the page can hand over its <audio> through
  // useMedia before this controller has connected.
  initialize() {
    this.onPlaying = () => {
      if (this.started) return
      this.started = true
      this.save()
    }
    this.onTimeUpdate = () => {
      if (this.started && Date.now() - this.lastSavedAt >= this.intervalValue) this.save()
    }
    this.onPauseOrSeek = () => {
      // The <video> pauses itself just before `ended`; that write is the end's.
      if (this.started && !this.media.ended) this.save()
    }
    this.onEnded = () => {
      this.completed = true
      this.save({ position: 0 })
    }
    this.mediaHandlers = {
      playing: this.onPlaying, timeupdate: this.onTimeUpdate, pause: this.onPauseOrSeek,
      seeked: this.onPauseOrSeek, ended: this.onEnded
    }
    this.onPageHide = () => this.flush()
    this.onVisibilityChange = () => {
      if (document.visibilityState === "hidden") this.flush()
    }
  }

  connect() {
    const media = this.media || this.element.querySelector(MEDIA_SELECTOR)
    if (!media) return

    this.started = false
    this.accessEnded = false
    this.completed = false
    this.lastSavedAt = 0
    this.writeCount = 0
    this.drawnWrite = 0

    this.media = null
    this.useMedia(media)
    window.addEventListener("pagehide", this.onPageHide)
    document.addEventListener("visibilitychange", this.onVisibilityChange)
  }

  disconnect() {
    if (!this.media) return

    this.flush()
    this.listenTo(this.media, "removeEventListener")
    window.removeEventListener("pagehide", this.onPageHide)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
  }

  // Follows another <video> or <audio> from now on — the one the page is showing.
  // The session stays started: switching is not starting again.
  useMedia(media) {
    if (!media || media === this.media) return

    if (this.media) this.listenTo(this.media, "removeEventListener")
    this.media = media
    this.listenTo(media, "addEventListener")
  }

  listenTo(media, method) {
    Object.entries(this.mediaHandlers).forEach(([type, handler]) => media[method](type, handler))
  }

  // A last write of where playback stands, unless the end already wrote it.
  flush() {
    if (this.started && !this.media.ended) this.save()
  }

  save({ position = this.media.currentTime } = {}) {
    if (this.accessEnded) return

    this.lastSavedAt = Date.now()

    const duration = this.media.duration
    if (Number.isFinite(duration) && duration > 0 && this.media.currentTime >= duration * COMPLETE_FRACTION) {
      this.completed = true
    }

    const body = new FormData()
    body.append("position_seconds", String(Math.max(0, Math.floor(Number(position) || 0))))
    body.append("completed", String(this.completed))

    const write = ++this.writeCount
    fetch(this.urlValue, {
      method: "PATCH",
      body,
      keepalive: true,
      credentials: "same-origin",
      headers: { "X-CSRF-Token": this.csrfToken, Accept: "text/vnd.turbo-stream.html" }
    }).then((response) => this.draw(response, write))
      .catch(() => { /* the next write carries the place */ })
  }

  // A redirected write (a lapsed login, lost access) ends on some other page;
  // only a Turbo Stream is this page's to draw.
  async draw(response, write) {
    if (response.status === 403) return this.endAccess(response)
    if (!response.ok || !response.headers.get("Content-Type")?.startsWith("text/vnd.turbo-stream.html")) return

    const stream = await response.text()
    if (!this.element.isConnected || write < this.drawnWrite) return

    this.drawnWrite = write
    Turbo.renderStreamMessage(stream)
  }

  // The viewer may no longer watch (their access ended, or the offering was
  // refunded): stop whatever is playing, and show the notice the reply carries.
  async endAccess(response) {
    if (this.accessEnded) return

    this.accessEnded = true
    this.element.querySelectorAll("video, audio").forEach((media) => media.pause())
    if (!response.headers.get("Content-Type")?.startsWith("text/vnd.turbo-stream.html")) return

    Turbo.renderStreamMessage(await response.text())
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
